import * as React from 'react';
import Text from '../../../shared/Text';
import FormValidationMessage from '../form-validation-message';
import { sendEmptyTextIfNull } from '../../utils/input-handlers';
import { useAuthData, useSetAuthData, useSubmitUsername } from '../providers/auth/hooks';
import { useRoutingData, useSetRoutingData } from '../providers/routing/hooks';
import { useResetPasswordData } from '../providers/resetPassword/hooks';
import { TextField } from '@mui/material';
import { AsyncButton } from '@c42/component-library';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from '../shared-form-components';

const LoginStepUsername: React.FC = () => {
  const setRoutingData = useSetRoutingData();
  const setAuthData = useSetAuthData();
  const submitUsername = useSubmitUsername();
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const {
    username,
    errorFetchingLoginConfig: error,
    isFetchingLoginConfig: isSubmittingUsername,
  } = useAuthData();
  const { usernameParam } = useRoutingData();
  const { notification } = useResetPasswordData();

  const changeUsernameHandler: React.FormEventHandler<any> = (event) => {
    event.persist?.();
    setAuthData((current) => ({
      ...current,
      username: (event.target as HTMLInputElement).value,
    }));
  };

  const submitForm = (e?: React.FormEvent<any>) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    setHasSubmitted(true);
    submitUsername();
  };

  React.useEffect(() => {
    if (usernameParam !== null) {
      changeUsernameHandler({ target: { value: usernameParam } } as any);
      setRoutingData((current) => ({
        ...current,
        usernameParam: null,
      }));
      submitForm();
    }
  }, []);

  const isInputDirty = username !== null;
  const isUsernameEmpty = !username;

  const errorText =
    isInputDirty && isUsernameEmpty && hasSubmitted ? Text.get('_validate_username_or_email') : '';

  return (
    <FormWrapper>
      <form onSubmit={submitForm} name="login-step1-form" data-test="login-step-username-form" noValidate>
        <FormValidationMessage
          message={error || notification}
          type={error ? 'error' : 'success'}
          dataTest="validation-msg"
        />

        <FormFieldWrapper>
          <TextField
            autoComplete="off"
            autoFocus
            disabled={isSubmittingUsername}
            error={Boolean(errorText)}
            fullWidth
            helperText={errorText}
            id="username"
            inputProps={{ 'data-test': 'username' }}
            label={Text.get('_placeholder_username_or_email')}
            onBlur={sendEmptyTextIfNull.bind(null, changeUsernameHandler, username)}
            onChange={changeUsernameHandler}
            value={username || ''}
          />
        </FormFieldWrapper>

        <FormSubmitWrapper>
          <AsyncButton data-test="submit" isLoading={isSubmittingUsername} type="submit" variant="contained">
            {Text.get('login_button_sign_in')}
          </AsyncButton>
        </FormSubmitWrapper>
      </form>
    </FormWrapper>
  );
};

export default LoginStepUsername;
